/**
 * TypeScript shapes for the public half of `subscription_plans` — the rows
 * `list_public_subscription_plans` returns for the pricing section.
 * Values are loaded from the database; these types hold no limit numbers.
 *
 * The workspace-entitlement half of this module (entitlement parsing, feature
 * gating, usage ratios) stays in the app repo — nothing on the marketing site
 * reads a workspace's entitlements.
 */

export type SyncMode = "manual" | "live"

export interface PlanLimits {
  /** Seat baseline. Hard cap when the plan has no seat_price_cents; a bundle with overage otherwise. */
  included_seats: number | null
  max_projects: number | null
  max_repo_connections: number | null
  max_scenarios: number | null
  sync_mode: SyncMode
  live_webhook_sync: boolean
  advanced_editor: boolean
  ai_copilot: boolean
  intelligent_search: boolean
  mcp_server: boolean
  status_automation: boolean
  custom_roles: boolean
  public_api: boolean
}

export interface PublicSubscriptionPlan {
  id: string
  name: string
  description: string
  priceCents: number | null
  priceInterval: string
  /** Cost of one seat beyond `limits.included_seats`, or null when the plan has no per-seat overage. */
  seatPriceCents: number | null
  limits: PlanLimits
  marketingFeatures: string[]
  sortOrder: number
}

/** A plan is free only when it has neither a base fee nor a per-seat charge. */
export function isFreePlan(plan: { priceCents: number | null; seatPriceCents: number | null }): boolean {
  const hasBasePrice = plan.priceCents !== null && plan.priceCents > 0
  const hasSeatPrice = plan.seatPriceCents !== null && plan.seatPriceCents > 0
  return !hasBasePrice && !hasSeatPrice
}

export function formatPlanPrice(priceCents: number | null): string {
  if (priceCents === null || priceCents === 0) return "$0"
  const dollars = priceCents / 100
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`
}

export function formatPlanPeriod(priceCents: number | null, priceInterval = "month"): string {
  if (priceCents === null || priceCents === 0) return "/mo"
  return priceInterval === "month" ? "/mo" : `/${priceInterval}`
}

/**
 * The headline price for a plan. A plan with no base fee but a seat price
 * (e.g. Pro: $0 + $4/seat) is billed purely per seat, so the seat rate IS
 * the headline -- not a footnote about overage beyond some included bucket.
 * A plan with a base fee shows that instead, regardless of seat price.
 */
export function formatPlanHeadline(
  priceCents: number | null,
  seatPriceCents: number | null,
  priceInterval = "month",
): { price: string; period: string } {
  const hasBasePrice = priceCents !== null && priceCents > 0
  if (!hasBasePrice && seatPriceCents !== null && seatPriceCents > 0) {
    return { price: formatPlanPrice(seatPriceCents), period: "/seat/mo" }
  }
  return { price: formatPlanPrice(priceCents), period: formatPlanPeriod(priceCents, priceInterval) }
}

export function parsePublicPlanRow(row: Record<string, unknown>): PublicSubscriptionPlan {
  return {
    id: String(row.id),
    name: String(row.name),
    description: String(row.description ?? ""),
    priceCents: row.price_cents === null || row.price_cents === undefined ? null : Number(row.price_cents),
    priceInterval: String(row.price_interval ?? "month"),
    seatPriceCents:
      row.seat_price_cents === null || row.seat_price_cents === undefined ? null : Number(row.seat_price_cents),
    limits: row.limits as PlanLimits,
    marketingFeatures: Array.isArray(row.marketing_features)
      ? row.marketing_features.map(String)
      : [],
    sortOrder: Number(row.sort_order ?? 0),
  }
}
